import axios from 'axios'
import pathJoin from './pathJoin'

/**
 * Rewrites images.pexels.com URLs to go through our backend proxy,
 * avoiding Cross-Origin-Resource-Policy blocks from Pexels.
 */
export function proxyPexelsImageUrl(url) {
  if (!url || typeof url !== 'string') return url
  try {
    const pexelsUrl = new URL(url)
    const pathSegments = pexelsUrl.pathname.split('/').filter(Boolean)
    const hasUnsafePathSegment = pathSegments.some((segment) => {
      const decodedSegment = decodeURIComponent(segment)
      return decodedSegment === '.' || decodedSegment === '..'
    })

    if (
      pexelsUrl.protocol !== 'https:' ||
      pexelsUrl.hostname !== 'images.pexels.com' ||
      !pexelsUrl.pathname.startsWith('/photos/') ||
      hasUnsafePathSegment
    ) {
      return url
    }

    return `/api/pexels/image${pexelsUrl.pathname}${pexelsUrl.search}`
  } catch (error) {
    return url
  }
}

function proxyImageSrcs(photo) {
  if (!photo?.src) return photo
  const proxiedSrc = {}
  for (const [key, value] of Object.entries(photo.src)) {
    proxiedSrc[key] = proxyPexelsImageUrl(value)
  }
  return { ...photo, src: proxiedSrc }
}

function proxyPhotosInResponse(data) {
  if (!data) return data
  if (data.photos) {
    return { ...data, photos: data.photos.map(proxyImageSrcs) }
  }
  if (data.media) {
    return { ...data, media: data.media.map(proxyImageSrcs) }
  }
  return data
}

const pexels = {
  search: {
    async getPhotos({ query, page }) {
      const response = await axios.get('/api/pexels/search', {
        params: {
          query,
          page,
          per_page: 12,
          orientation: 'landscape',
        },
        withCredentials: true,
      })
      return { response: proxyPhotosInResponse(response.data) }
    },
  },
  topics: {
    async getPhotos({ topicIdOrSlug, page, sort = 'asc' }) {
      const response = await axios.get(`/api/pexels/topic/${topicIdOrSlug}`, {
        params: {
          page,
          per_page: 12,
          orientation: 'landscape',
          sort,
        },
        withCredentials: true,
      })

      return { response: proxyPhotosInResponse(response.data) }
    },
  },
  photos: {
    async trackDownload(image) {
      const pexelsDownloadLocationUrl = image?.links?.download_location
      const { pathname } = new URL(pexelsDownloadLocationUrl)
      return axios.get(pathJoin('/api/pexels', pathname))
    },
  },
}

export default pexels
